import { useEffect, useMemo } from "react"

import {
  contractorsApi,
  getContractorProfile,
  useCreateContractorPartnerMutation,
  useDeleteContractorPartnerMutation,
  useLazyGetContractorProfileQuery,
  useUpdateContractorPartnerMutation,
} from "store/api/contractorsApi"
import { useLazyGetPartnerCategoriesQuery } from "store/api/dictionaryApi"
import { useAppDispatch, useAppSelector } from "store/hooks"

import { ContractorPartnersForm } from "components/ContractorPartnersForm"

export const AccountSettingsPartners = () => {
  const dispatch = useAppDispatch()
  const [triggerGetContractorProfile, { data: fetchedProfileData }] = useLazyGetContractorProfileQuery()
  const [triggerGetPartnerCategories, { data: categoriesData }] = useLazyGetPartnerCategoriesQuery()
  const selectContractorProfile = useMemo(() => getContractorProfile.select(), [])
  const contractorProfile = useAppSelector(selectContractorProfile)
  const [createContractorPartner, { isLoading: isCreating }] = useCreateContractorPartnerMutation()
  const [updateContractorPartner, { isLoading: isUpdating }] = useUpdateContractorPartnerMutation()
  const [deleteContractorPartner, { isLoading: isDeleting }] = useDeleteContractorPartnerMutation()
  const isSaving = isCreating || isUpdating || isDeleting

  const profileData = contractorProfile?.data ? contractorProfile?.data : fetchedProfileData

  useEffect(() => {
    if (!profileData) {
      triggerGetContractorProfile()
    }
    triggerGetPartnerCategories()
  }, [profileData, triggerGetContractorProfile, triggerGetPartnerCategories])

  const isLoading = !profileData || !categoriesData
  const partnerCategories = categoriesData?.data ?? []

  const initialPartners = useMemo(
    () => profileData?.data?.contractor_partners ?? [],
    [profileData?.data?.contractor_partners],
  )

  const api = useMemo(
    () => ({
      createPartner: (body: FormData) => createContractorPartner(body).unwrap(),
      updatePartner: (id: number, body: FormData) => updateContractorPartner({ id, body }).unwrap(),
      deletePartner: (id: number) => deleteContractorPartner(id).unwrap(),
      onPartnerChangesSettled: async () => {
        dispatch(contractorsApi.util.invalidateTags(["contractor-profile"]))
        triggerGetContractorProfile()
      },
    }),
    [createContractorPartner, deleteContractorPartner, dispatch, triggerGetContractorProfile, updateContractorPartner],
  )

  return (
    <ContractorPartnersForm
      categories={partnerCategories}
      excludeFirstCategory
      isLoading={isLoading}
      initialPartners={initialPartners}
      isSaving={isSaving}
      api={api}
    />
  )
}
